<template>
  <NForm class="content-selector-ai-images h-full flex flex-col">
    <NGrid class="h-full overflow-hidden mt-3" cols="12" item-responsive>
      <NGridItem class="h-full overflow-auto" :span="'12 650:7 1200:8 1400:9'">
        <div class="mr-3 h-full">
          <div class="flex flex-col gap-3 mb-3">
            <NFormItem
              :label="
                mediaType === 'image'
                  ? t('canvasUI.contentSelector.aiImages.addImagePrompt')
                  : t('canvasUI.contentSelector.aiImages.addVideoPrompt')
              "
              path="prompt"
              :rule="{
                trigger: ['input', 'blur'],
              }"
            >
              <NInput
                v-model:value="prompt"
                clearable
                :disabled="isGenerating"
                placeholder=""
                @keyup.enter="onGenerateOrAdd"
              />
            </NFormItem>

            <NFormItem :label="t('canvasUI.canvasBuilder.multimediaSettings.chooseMediaType')" path="mediaType">
              <NSelect
                v-model:value="mediaType"
                :disabled="isGenerating"
                :options="[
                  { label: t('canvasUI.canvasBuilder.multimediaSettings.image'), value: 'image' },
                  { label: t('canvasUI.canvasBuilder.multimediaSettings.video'), value: 'video' },
                ]"
              />
            </NFormItem>
          </div>

          <div class="flex justify-between w-full mb-3">
            <div v-if="error" class="text-error text-sm">{{ error }}</div>
            <div class="flex-grow"></div>
            <CButton
              :disabled="isGenerating || !prompt"
              :loading="isGenerating"
              type="primary"
              @click="onGenerateOrAdd"
            >
              {{ buttonText }}
            </CButton>
          </div>

          <div v-if="showPreview" class="ai-preview-container mt-3">
            <div class="ai-preview-wrapper">
              <div v-if="isGenerating" class="text-center py-5">
                <NSpin />
                <div class="mt-2">{{ t('canvasUI.contentSelector.aiImages.loadingMessage') }}</div>
              </div>
              <div v-else-if="mediaUrl" class="ai-media-preview">
                <img
                  v-if="mediaType === 'image'"
                  alt="AI Generated Image"
                  class="ai-thumbnail"
                  :src="mediaUrl"
                  @error="onMediaLoadError"
                  @load="onMediaLoad"
                />
                <video
                  v-else-if="mediaType === 'video'"
                  class="ai-thumbnail"
                  controls
                  :src="mediaUrl"
                  @error="onMediaLoadError"
                  @loadeddata="onMediaLoad"
                >
                  {{ t('canvasUI.contentSelector.mediaPreview.noVideoBrowser') }}
                </video>
                <div v-if="isMediaGenerated" class="ai-item-overlay">
                  <CButton class="ai-select-btn" size="small" type="primary" @click="onAdd">
                    {{ t('canvasUI.common.select') }}
                  </CButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </NGridItem>
      <NGridItem class="h-full overflow-auto" :span="'12 650:5 1200:4 1400:3'">
        <SelectionPanel />
      </NGridItem>
    </NGrid>
  </NForm>
</template>

<script setup lang="ts">
import { NGridItem, NGrid, NFormItem, NInput, NForm, NSelect, NSpin } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { computed, ref, inject, watch, type ComputedRef } from 'vue'
import axios from 'axios'

import CButton from '@lib/components/CButton/CButton.vue'
import SelectionPanel from '@content-selector/components/Content/SelectionPanel/SelectionPanel.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { type PitcherInfo } from '@lib/types/app'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const emit = defineEmits<{
  add: [{ url: string; content_type: 'image' | 'video' }]
}>()

const { t } = useI18n()

const pitcherInfo = inject<ComputedRef<Partial<PitcherInfo>>>(
  'pitcherInfo',
  computed(() => ({ apiOrigin: '' })),
)

// Get Launch Darkly flags
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

// Models for media generation with fallbacks
const IMAGE_MODEL = computed(() => launchDarkly.value.ai_image_generation_model || 'minimax/image-01')
const VIDEO_MODEL = computed(() => launchDarkly.value.ai_video_generation_model || 'minimax/video-01')

const mediaType = ref<'image' | 'video'>('image')
const prompt = ref('')
const mediaUrl = ref('')
const showPreview = ref(false)
const isMediaGenerated = ref(false)
const isGenerating = ref(false)
const error = ref('')
const themeVars = useThemeVars()

const buttonText = computed(() => {
  if (isGenerating.value) return t('canvasUI.contentSelector.aiImages.generating')
  return isMediaGenerated.value ? t('canvasUI.common.add') : t('canvasUI.contentSelector.aiImages.generate')
})

// API endpoints for the Next Core AI Generation API
const aiGenerationApiBaseUrl = computed(() => `${pitcherInfo.value?.apiOrigin}/core/api/protected/ai/ai-generation`)

watch([prompt, mediaType], () => {
  isMediaGenerated.value = false
  showPreview.value = false
  error.value = ''
})

// Generate media (image or video) using the API
async function generateMedia() {
  try {
    isGenerating.value = true
    error.value = ''

    // Select the appropriate model based on media type
    const model = mediaType.value === 'image' ? IMAGE_MODEL.value : VIDEO_MODEL.value

    // Common input parameters
    const inputParams: Record<string, any> = {
      prompt: prompt.value,
      negative_prompt: 'ugly, blurry, low quality',
    }

    // Model-specific parameters
    if (mediaType.value === 'image') {
      inputParams.width = 768
      inputParams.height = 768
      inputParams.scheduler = 'K_EULER'
      inputParams.num_outputs = 1
      inputParams.guidance_scale = 7.5
    } else {
      // Video-specific parameters for minimax/video-01
      // Adjust as needed based on the model's requirements
      // inputParams.num_frames = 120
    }

    // Step 1: Create the prediction
    const predictionResponse = await axios.post(
      aiGenerationApiBaseUrl.value,
      {
        version: model,
        input: inputParams,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pitcherInfo.value?.accessToken}`,
        },
      },
    )

    // Step 2: Get the prediction ID
    const predictionId = predictionResponse.data.id
    if (!predictionId) {
      throw new Error(`Failed to start ${mediaType.value} generation`)
    }

    // Step 3: Poll for the result
    let attempts = 0
    const MAX_ATTEMPTS = 20 // 3.33 minutes (20 x 10s) - increased for video which may take longer

    while (attempts < MAX_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, 10000)) // Wait 10 seconds between checks
      attempts++

      // Check prediction status
      const statusResponse = await axios.get(`${aiGenerationApiBaseUrl.value}/${predictionId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pitcherInfo.value?.accessToken}`,
        },
      })

      // If the prediction succeeded, return the media URL
      if (statusResponse.data.status === 'succeeded' && statusResponse.data.output) {
        // Handle array or single URL output
        const outputUrl = Array.isArray(statusResponse.data.output)
          ? statusResponse.data.output[0]
          : statusResponse.data.output

        return outputUrl
      }

      // If the prediction failed, throw an error
      if (statusResponse.data.status === 'failed') {
        throw new Error(`${mediaType.value} generation failed`)
      }
    }

    // If we've exceeded the maximum number of attempts, throw a timeout error
    throw new Error(`${mediaType.value} generation timed out`)
  } catch (e) {
    console.error(`Error generating ${mediaType.value}:`, e)
    throw e
  } finally {
    isGenerating.value = false
  }
}

async function onGenerateOrAdd() {
  if (!prompt.value) return

  if (isMediaGenerated.value) {
    onAdd()
  } else {
    try {
      showPreview.value = true
      mediaUrl.value = await generateMedia()
    } catch (e) {
      showPreview.value = false
      error.value = e instanceof Error ? e.message : `Failed to generate ${mediaType.value}`
    }
  }
}

function onMediaLoad() {
  isMediaGenerated.value = true
}

function onMediaLoadError() {
  isMediaGenerated.value = false
  error.value = `Failed to load ${mediaType.value}`
}

function onAdd() {
  emit('add', { url: mediaUrl.value, content_type: mediaType.value })
  prompt.value = ''
  mediaUrl.value = ''
  showPreview.value = false
  isMediaGenerated.value = false
}
</script>

<style lang="scss" scoped>
.ai-preview-container {
  width: 100%;
}

.ai-preview-wrapper {
  max-width: 400px;
  margin: 0 auto;
}

.ai-media-preview {
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: 4px;

  &:hover {
    .ai-item-overlay {
      opacity: 1;
    }
  }
}

.ai-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ai-item-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.ai-select-btn {
  transform: translateY(0);
  transition: transform 0.2s ease-in-out;

  .ai-item-overlay:hover & {
    transform: translateY(-4px);
  }
}
.ai-preview-container {
  margin: 0 auto;
  max-width: 600px;
}

.ai-preview-wrapper {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background-color: v-bind('themeVars.primary6');
}
</style>
